import { morph } from '@theme/morph';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * @param {string} sectionId
 * @returns {string}
 */
function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '')}`;
}

/** @type {Promise<void>} */
let cartMutationQueue = Promise.resolve();

/**
 * @param {() => Promise<unknown>} task
 * @returns {Promise<unknown>}
 */
export function enqueueCartMutation(task) {
  const run = cartMutationQueue.then(task, task);
  cartMutationQueue = run.then(
    () => {},
    () => {}
  );
  return run;
}

/**
 * @param {boolean} locked
 */
export function setCartItemsLocked(locked) {
  document.querySelectorAll('cart-items-component').forEach((element) => {
    element.classList.toggle('cart-items-disabled', locked);
  });
}

/**
 * @param {string} [primarySectionId]
 * @returns {string}
 */
export function collectCartSectionIds(primarySectionId) {
  /** @type {Set<string>} */
  const sectionIds = new Set();

  if (primarySectionId) {
    sectionIds.add(primarySectionId);
  }

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.add(item.dataset.sectionId);
    }
  });

  return Array.from(sectionIds).join(',');
}

/**
 * @param {string} url
 * @param {object} body
 * @returns {Promise<object>}
 */
export async function requestCartJson(url, body) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
  const text = await response.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Cart request returned non-JSON (${response.status})`);
  }

  if (!response.ok && !data?.status) {
    throw new Error(data?.description || data?.message || `Cart request failed (${response.status})`);
  }

  return data;
}

/**
 * @param {string} html
 * @returns {number}
 */
export function extractCartItemCountFromHtml(html) {
  if (!html) return 0;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const countEl = doc.querySelector('[ref="cartItemCount"]');

  return countEl ? parseInt(countEl.textContent || '0', 10) : 0;
}

/**
 * @param {Record<string, string> | undefined} sections
 * @param {string} sectionId
 * @returns {number}
 */
export function extractCartItemCountFromSections(sections, sectionId) {
  if (!sections?.[sectionId]) return 0;
  return extractCartItemCountFromHtml(sections[sectionId]);
}

/**
 * @param {Record<string, string>} sections
 */
export function syncCartDrawerDialogFromSections(sections) {
  for (const html of Object.values(sections)) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const newDialog = fragment.querySelector('.cart-drawer__dialog');
    const existingDialog = document.querySelector('.cart-drawer__dialog');

    if (!(newDialog instanceof HTMLElement) || !(existingDialog instanceof HTMLElement)) continue;

    existingDialog.className = newDialog.className;

    const ariaLabelledBy = newDialog.getAttribute('aria-labelledby');
    if (ariaLabelledBy) {
      existingDialog.setAttribute('aria-labelledby', ariaLabelledBy);
    } else {
      existingDialog.removeAttribute('aria-labelledby');
    }

    return;
  }
}

/**
 * Morph every cart-items-component for each section in the Cart API response.
 * Always replaces the full component (not partial subtrees) so drawer regions,
 * summary, and empty/filled toggles stay in sync.
 *
 * @param {Record<string, string>} sections
 * @returns {boolean}
 */
export function morphCartSections(sections) {
  if (!sections || Object.keys(sections).length === 0) return false;

  let didMorph = false;

  for (const [sectionId, html] of Object.entries(sections)) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const sectionRoot = fragment.getElementById(buildSectionSelector(sectionId));

    if (!sectionRoot) continue;

    const newCartItems = sectionRoot.querySelector('cart-items-component');
    if (!newCartItems) continue;

    const existingComponents = document.querySelectorAll(
      `cart-items-component[data-section-id="${CSS.escape(sectionId)}"]`
    );

    if (existingComponents.length === 0) continue;

    for (const existing of existingComponents) {
      morph(existing, newCartItems);
      didMorph = true;
    }
  }

  if (didMorph) {
    syncCartDrawerDialogFromSections(sections);
    document.querySelectorAll('scroll-hint.cart-drawer__items').forEach((scrollHint) => {
      scrollHint.dispatchEvent(new Event('scroll'));
    });
  }

  return didMorph;
}

/**
 * @typedef {object} ApplyCartSectionsOptions
 * @property {string} sourceSectionId
 * @property {string} [source]
 * @property {object} [resource] - Cart API response body
 * @property {number} [itemCount]
 * @property {boolean} [morph] - Default true
 * @property {boolean} [dispatchEvent] - Default true
 * @property {EventTarget | null} [eventTarget] - Target for CartUpdateEvent (for self-skip in listeners)
 * @property {boolean} [skipDepositCleanup] - Passed on event detail
 */

/**
 * @param {object} response
 * @param {Record<string, string>} [response.sections]
 * @param {ApplyCartSectionsOptions} options
 * @returns {boolean} Whether section HTML was morphed
 */
export function applyCartSectionsResponse(response, options) {
  const {
    sourceSectionId,
    source,
    resource = response,
    itemCount,
    morph = true,
    dispatchEvent = true,
    eventTarget = null,
    skipDepositCleanup = false,
  } = options;

  const sections = response?.sections;
  let morphed = false;

  if (morph && sections) {
    morphed = morphCartSections(sections);
  }

  if (dispatchEvent) {
    const resolvedItemCount =
      itemCount ??
      extractCartItemCountFromSections(sections, sourceSectionId);

    const updateEvent = new CartUpdateEvent(resource, sourceSectionId, {
      source,
      sections,
      itemCount: resolvedItemCount,
      morphHandled: morphed,
      skipDepositCleanup,
    });

    if (eventTarget) {
      eventTarget.dispatchEvent(updateEvent);
    } else {
      document.dispatchEvent(updateEvent);
    }
  }

  return morphed;
}

/**
 * @param {string} sectionId
 * @param {string} html
 * @returns {boolean}
 */
export function morphCartSection(sectionId, html) {
  return morphCartSections({ [sectionId]: html });
}
